import React, { useState, useEffect } from 'react';
import { Box, Typography, TextField, Button, Grid, Divider } from '@mui/material';
import { Save as SaveIcon } from '@mui/icons-material';
import { useAuth } from '../../../../contexts/AuthContext';
import SignaturePad from '../SignaturePad';
import ManagerSelect from '../ManagerSelect';
import { RichText, getFormText, NO_AUTOFILL } from '../PolicyText';

const GasAirMilesCardForm = ({ language = 'en', onSubmit, loading, disabled }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    employeeName: '',
    card1: '',
    card2: '',
    card3: '',
    managerName: ''
  });
  const [employeeSignature, setEmployeeSignature] = useState(null);
  const [managerSignature, setManagerSignature] = useState(null);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (user) {
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.username;
      setFormData(prev => ({ ...prev, employeeName: fullName }));
    }
  }, [user]);

  const labels = language === 'es' ? {
    employeeName: 'Nombre del Empleado',
    employeeSignature: 'Firma del Empleado',
    managerSignature: 'Firma del Gerente',
    save: 'Guardar',
    required: 'Obligatorio',
    signatureRequired: 'Se requiere firma'
  } : {
    employeeName: 'Employee Name',
    employeeSignature: 'Employee Signature',
    managerSignature: 'Manager Signature',
    save: 'Save Form',
    required: 'Required',
    signatureRequired: 'Signature required'
  };

  // Legacy PL Hub agreement wording and card labels
  // (translation.json employeeForms.text.gasCard).
  const wording = getFormText(language, 'text.gasCard');

  const validate = () => {
    const newErrors = {};
    if (!formData.employeeName.trim()) newErrors.employeeName = labels.required;
    if (!formData.card1.trim()) newErrors.card1 = labels.required;
    if (!formData.managerName.trim()) newErrors.managerName = labels.required;
    if (!employeeSignature) newErrors.employeeSignature = labels.signatureRequired;
    if (!managerSignature) newErrors.managerSignature = labels.signatureRequired;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) {
      onSubmit({ ...formData, employeeSignature, managerSignature }, employeeSignature);
    }
  };

  const cardField = (name, label, required) => (
    <Grid size={{ xs: 12, sm: 4 }}>
      <TextField fullWidth label={label} name={name} value={formData[name]}
        onChange={(e) => setFormData({ ...formData, [name]: e.target.value })} required={required} disabled={disabled}
        error={!!errors[name]} helperText={errors[name]} />
    </Grid>
  );

  return (
    <Box component="form" onSubmit={handleSubmit} autoComplete="off" data-lpignore="true" data-form-type="other">
      <Typography variant="body2" sx={{ mb: 2 }}>
        <RichText text={wording.before} values={{ name: formData.employeeName }} />
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        {cardField('card1', wording.card1, true)}
        {cardField('card2', wording.card2, false)}
        {cardField('card3', wording.card3, false)}
      </Grid>

      <Typography variant="body2" sx={{ mb: 3 }}>
        <RichText text={wording.after} values={{ name: formData.employeeName }} />
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField fullWidth label={labels.employeeName} name="employeeName" value={formData.employeeName}
            onChange={(e) => setFormData({...formData, employeeName: e.target.value})} required disabled={disabled} inputProps={NO_AUTOFILL}
            error={!!errors.employeeName} helperText={errors.employeeName} />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <SignaturePad onSignature={setEmployeeSignature} label={labels.employeeSignature} language={language} />
      {errors.employeeSignature && <Typography color="error" variant="caption">{errors.employeeSignature}</Typography>}

      <Divider sx={{ my: 3 }} />

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <ManagerSelect
            label={wording.managerName}
            value={formData.managerName}
            onChange={(managerName) => setFormData({ ...formData, managerName })}
            required
            disabled={disabled}
            error={!!errors.managerName}
            helperText={errors.managerName}
            language={language}
          />
        </Grid>
      </Grid>

      <SignaturePad onSignature={setManagerSignature} label={labels.managerSignature} language={language} />
      {errors.managerSignature && <Typography color="error" variant="caption">{errors.managerSignature}</Typography>}

      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="submit" variant="contained" size="large" startIcon={<SaveIcon />} disabled={disabled || loading}>
          {labels.save}
        </Button>
      </Box>
    </Box>
  );
};

export default GasAirMilesCardForm;
